// PageTransition.js
import { motion } from "motion/react";
import { useLocation } from "react-router";
import { Suspense, useLayoutEffect, useRef, useState, useEffect } from "react";
import FallbackLoader from "./loader.page";
import { useIsMobile } from "@hooks/useIsMobile";

export default function PageTransition({ children }) {
  const location = useLocation();
  const isMobile = useIsMobile();

  const stackRef = useRef([]);
  const idxRef = useRef(-1);

  const getId = (loc) => loc.key ?? loc.pathname;
  const currentId = getId(location);
  const currentPath = location.pathname;

  const NAV_TABS_DESKTOP = [
    "/dashboard",
    "/jobs",
    "/inbox",
    "/artists",
    "/profile",
    "/discover",
    "/settings",
  ];
  const NAV_TABS_MOBILE = [
    "/dashboard",
    "/jobs",
    "/discover",
    "/profile",
    "/settings",
  ];

  const NAV_ORDER_DESKTOP = {
    "/dashboard": 0,
    "/jobs": 1,
    "/inbox": 2,
    "/artists": 3,
    "/profile": 4,
    "/discover": 5,
    "/settings": 6,
  };
  const NAV_ORDER_MOBILE = {
    "/dashboard": 0,
    "/jobs": 1,
    "/discover": 2,
    "/profile": 3,
    "/settings": 4,
  };

  const [NAV_TABS, setNavTabs] = useState(
    isMobile ? NAV_TABS_MOBILE : NAV_TABS_DESKTOP,
  );
  const [NAV_ORDER, setNavOrder] = useState(
    isMobile ? NAV_ORDER_MOBILE : NAV_ORDER_DESKTOP,
  );

  useEffect(() => {
    setNavTabs(isMobile ? NAV_TABS_MOBILE : NAV_TABS_DESKTOP);
    setNavOrder(isMobile ? NAV_ORDER_MOBILE : NAV_ORDER_DESKTOP);
  }, [isMobile]);

  const isExactNavTab = (path) => NAV_TABS.includes(path);

  const localDirection = (() => {
    if (stackRef.current.length === 0) return "forward";
    const currentIdx = idxRef.current;
    const prevEntry = stackRef.current[currentIdx];
    if (prevEntry.id === currentId)
      return stackRef.current._lastDirection ?? "forward";

    const prevPath = prevEntry.pathname;
    const isPrevTab = isExactNavTab(prevPath);
    const isNextTab = isExactNavTab(currentPath);

    if (isPrevTab && isNextTab) {
      return NAV_ORDER[currentPath] > NAV_ORDER[prevPath]
        ? "forward"
        : "backward";
    }

    const foundIndex = stackRef.current.findIndex((it) => it.id === currentId);
    if (foundIndex === -1) return "forward";
    return foundIndex > currentIdx ? "forward" : "backward";
  })();

  useLayoutEffect(() => {
    const id = currentId;
    const pathname = currentPath;

    if (stackRef.current.length === 0) {
      stackRef.current.push({ id, pathname });
      idxRef.current = 0;
      stackRef.current._lastDirection = "forward";
      return;
    }

    const currentIdx = idxRef.current;
    if (stackRef.current[currentIdx].id === id) return;

    const foundIndex = stackRef.current.findIndex((it) => it.id === id);

    if (foundIndex === -1) {
      if (currentIdx < stackRef.current.length - 1) {
        stackRef.current = stackRef.current.slice(0, currentIdx + 1);
      }
      stackRef.current.push({ id, pathname });
      idxRef.current = stackRef.current.length - 1;
      stackRef.current._lastDirection = "forward";
    } else {
      idxRef.current = foundIndex;
      stackRef.current._lastDirection =
        foundIndex > currentIdx ? "forward" : "backward";
    }
  }, [location.key, location.pathname]);

  const variants = {
    initial: (dir) => ({
      opacity: 0, // just fade instead of sliding from 100vw
      position: "absolute",
      width: "100%",
    }),
    animate: {
      opacity: 1,
      position: "relative",
      transition: { duration: 0.18, ease: [0.33, 1, 0.68, 1] },
    },
    exit: (dir) => ({
      x: dir === "forward" ? "-30vw" : "30vw", // subtle slide on exit only
      opacity: 0,
      position: "absolute",
      width: "100%",
      transition: { duration: 0.18, ease: [0.33, 1, 0.68, 1] },
    }),
  };

  return (
  <motion.div
    key={location.key ?? location.pathname}
    custom={localDirection}
    variants={variants}
    initial="initial"
    animate="animate"
    exit="exit"
    style={{
      position: "relative",
      overflow: "hidden",
      minHeight: "100%",
      height: "100%",
    }}
  >
    <Suspense fallback={<FallbackLoader />}>
      {children}
    </Suspense>
  </motion.div>
);
}
